import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { fightersSchedule2026, opponentShortNames, monthLabels, type GameDay } from "@/mocks/fightersSchedule";

const DAYS_OF_WEEK = ["日", "月", "火", "水", "木", "金", "土"];

interface ParkingDateRow {
  game_date: string;
  status: string;
}

function formatDateStr(y: number, m: number, d: number) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

interface ParkingStatusStyle {
  label: string;
  short: string;
  dot: string;
  badge: string;
}

function getStatusStyle(status: string | undefined): ParkingStatusStyle {
  switch (status) {
    case "full":
      return {
        label: "満車",
        short: "満",
        dot: "bg-primary-500",
        badge: "bg-primary-100 text-primary-700",
      };
    case "few":
      return {
        label: "残りわずか",
        short: "残",
        dot: "bg-secondary-500",
        badge: "bg-secondary-100 text-secondary-700",
      };
    case "available":
    default:
      return {
        label: "空きあり",
        short: "空",
        dot: "bg-accent-500",
        badge: "bg-accent-100 text-accent-700",
      };
  }
}

function buildGamesMap(): Map<string, GameDay> {
  const map = new Map<string, GameDay>();
  fightersSchedule2026.forEach((g) => map.set(g.date, g));
  return map;
}

export default function ParkingCalendar() {
  const today = new Date();
  const todayStr = formatDateStr(today.getFullYear(), today.getMonth() + 1, today.getDate());

  const [viewYear, setViewYear] = useState(2026);
  const [viewMonth, setViewMonth] = useState(() => {
    const m = today.getFullYear() === 2026 ? today.getMonth() + 1 : 3;
    return m;
  });

  const [statusMap, setStatusMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const gamesMap = useMemo(() => buildGamesMap(), []);

  const fetchStatuses = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data, error: fetchError } = await supabase
        .from("parking_dates")
        .select("game_date, status");

      if (fetchError) {
        setError(true);
        return;
      }

      const map = new Map<string, string>();
      (data as ParkingDateRow[]).forEach((row) => {
        map.set(row.game_date, row.status);
      });
      setStatusMap(map);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  const goToPrevMonth = () => {
    if (viewMonth === 1) {
      setViewYear((y) => y - 1);
      setViewMonth(12);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 12) {
      setViewYear((y) => y + 1);
      setViewMonth(1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const firstDay = new Date(viewYear, viewMonth - 1, 1);
  const startDow = firstDay.getDay();
  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();

  const calendarCells: (number | null)[] = [];
  for (let i = 0; i < startDow; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  const upcomingGames = useMemo(() => {
    return fightersSchedule2026
      .filter((g) => g.date >= todayStr)
      .slice(0, 8);
  }, [todayStr]);

  const jumpToReserve = () => {
    const el = document.getElementById("reserve");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="parking" className="bg-background-50 py-24 md:py-32 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="text-center">
          <span className="font-heading italic text-primary-600 text-sm tracking-[0.3em] uppercase">
            Parking Availability
          </span>
          <h2 className="mt-4 font-heading text-2xl md:text-4xl lg:text-5xl leading-tight text-foreground-950">
            駐車場の<span className="italic">空き状況</span>
          </h2>
          <p className="mt-4 text-foreground-700 text-sm md:text-base leading-relaxed max-w-xl mx-auto">
            試合日ごとの駐車場の空き状況をカレンダーで確認できます。
            満車の日は受付を終了していますので、お早めのご予約をおすすめします。
          </p>
        </div>

        <div className="mt-12 md:mt-16 flex flex-col lg:flex-row gap-8 md:gap-10">
          {/* Calendar Panel */}
          <div className="lg:w-[420px] shrink-0">
            <div className="bg-background-100 rounded-3xl overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
                <button
                  onClick={goToPrevMonth}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-200 text-foreground-600 hover:text-foreground-950 cursor-pointer transition-colors"
                  aria-label="前の月"
                >
                  <i className="ri-arrow-left-s-line text-lg" />
                </button>
                <span className="font-heading text-lg text-foreground-950">
                  {viewYear}年{monthLabels[viewMonth]}
                </span>
                <button
                  onClick={goToNextMonth}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-200 text-foreground-600 hover:text-foreground-950 cursor-pointer transition-colors"
                  aria-label="次の月"
                >
                  <i className="ri-arrow-right-s-line text-lg" />
                </button>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-7 mb-2">
                  {DAYS_OF_WEEK.map((dow) => (
                    <div
                      key={dow}
                      className={`text-center text-[11px] font-medium py-1 ${
                        dow === "日"
                          ? "text-red-500"
                          : dow === "土"
                            ? "text-blue-500"
                            : "text-foreground-500"
                      }`}
                    >
                      {dow}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-y-1">
                  {calendarCells.map((day, idx) => {
                    if (day === null) {
                      return <div key={`empty-${idx}`} className="min-h-[4.5rem]" />;
                    }
                    const dateStr = formatDateStr(viewYear, viewMonth, day);
                    const game = gamesMap.get(dateStr);
                    const isPast = dateStr < todayStr;
                    const isToday = dateStr === todayStr;
                    const dowIdx = new Date(viewYear, viewMonth - 1, day).getDay();
                    const isSunday = dowIdx === 0;
                    const isSaturday = dowIdx === 6;
                    const isHoliday = game?.isHoliday;
                    const status = statusMap.get(dateStr);
                    const isFull = status === "full";
                    const isAvailable = status === "available";
                    const shortName = game ? (opponentShortNames[game.opponent] || game.opponent) : null;
                    const isReservable = !!game && !isPast;

                    const cellContent = (
                      <>
                        <span
                          className={`text-sm leading-none ${
                            isToday
                              ? "font-bold text-primary-600"
                              : isHoliday || isSunday
                                ? "text-red-500"
                                : isSaturday
                                  ? "text-blue-500"
                                  : "text-foreground-800"
                          } ${isPast && !isToday ? "opacity-40" : ""}`}
                        >
                          {day}
                        </span>
                        {game && status && (
                          <span
                            className={`mt-1 text-[10px] font-medium leading-tight rounded-full px-1.5 py-0.5 whitespace-nowrap ${
                              isPast
                                ? "bg-background-200 text-foreground-400"
                                : getStatusStyle(status).badge
                            } ${isPast ? "opacity-60" : ""}`}
                          >
                            {getStatusStyle(status).short}
                          </span>
                        )}
                        {shortName && (
                          <span
                            className={`mt-0.5 text-[10px] leading-tight whitespace-nowrap ${
                              isPast ? "opacity-40 text-foreground-400" : "text-foreground-600"
                            }`}
                          >
                            {shortName}
                          </span>
                        )}
                      </>
                    );

                    const cellClass = `min-h-[4.5rem] flex flex-col items-center justify-center py-1 relative rounded-xl transition-colors ${
                      isReservable ? "hover:bg-background-200 cursor-pointer" : ""
                    }`;

                    if (isReservable) {
                      return (
                        <Link
                          key={dateStr}
                          to={`?game_date=${dateStr}#reserve`}
                          onClick={jumpToReserve}
                          aria-label={`${viewYear}/${viewMonth}/${day}の試合日を予約する`}
                          className={cellClass}
                        >
                          {cellContent}
                        </Link>
                      );
                    }

                    return (
                      <div key={dateStr} className={cellClass}>
                        {cellContent}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs text-foreground-700 bg-accent-100 text-accent-800 rounded-full px-3 py-1.5">
                <span className="w-2 h-2 rounded-full bg-accent-500" />
                空きあり
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-foreground-700 bg-secondary-100 text-secondary-800 rounded-full px-3 py-1.5">
                <span className="w-2 h-2 rounded-full bg-secondary-500" />
                残りわずか
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-foreground-700 bg-primary-100 text-primary-800 rounded-full px-3 py-1.5">
                <span className="w-2 h-2 rounded-full bg-primary-500" />
                満車
              </span>
            </div>
          </div>

          {/* Upcoming Games List */}
          <div className="flex-1">
            <h3 className="font-heading text-lg text-foreground-950 mb-4">
              直近の試合日の空き状況
            </h3>

            {loading ? (
              <div className="bg-background-100 rounded-3xl p-10 text-center">
                <p className="text-foreground-500 text-sm">空き状況を読み込み中...</p>
              </div>
            ) : error ? (
              <div className="bg-background-100 rounded-3xl p-10 text-center">
                <span className="w-14 h-14 flex items-center justify-center mx-auto rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-alert-line text-2xl" />
                </span>
                <p className="mt-4 text-foreground-600 text-sm">
                  空き状況を取得できませんでした
                </p>
                <button
                  type="button"
                  onClick={fetchStatuses}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-secondary-100 text-secondary-800 text-sm font-medium hover:bg-secondary-200 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-refresh-line" />
                  再読み込み
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {upcomingGames.map((game) => {
                  const isToday = game.date === todayStr;
                  const dateObj = new Date(game.date);
                  const md = `${dateObj.getMonth() + 1}/${dateObj.getDate()}`;
                  const listMeta = getStatusStyle(statusMap.get(game.date));

                  return (
                    <Link
                      key={game.date}
                      to={`?game_date=${game.date}#reserve`}
                      onClick={jumpToReserve}
                      className={`flex items-center gap-4 bg-background-100 hover:bg-background-200 rounded-2xl px-4 py-3.5 transition-colors cursor-pointer ${
                        isToday ? "ring-2 ring-primary-500" : ""
                      }`}
                    >
                      <div className="w-14 shrink-0 text-center">
                        <div
                          className={`text-xs ${
                            game.isHoliday || game.dayOfWeek === "日"
                              ? "text-red-500"
                              : game.dayOfWeek === "土"
                                ? "text-blue-500"
                                : "text-foreground-500"
                          }`}
                        >
                          {md}（{game.dayOfWeek}）
                        </div>
                        <div className="text-base font-heading text-foreground-950 mt-0.5">
                          {game.time}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-foreground-800 whitespace-nowrap">
                          {game.opponent}
                        </div>
                        {isToday && (
                          <span className="text-[11px] text-primary-600 font-medium">
                            本日
                          </span>
                        )}
                      </div>

                      <span
                        className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 whitespace-nowrap shrink-0 ${listMeta.badge}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${listMeta.dot}`} />
                        {listMeta.label}
                      </span>

                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                        <i className="ri-arrow-right-line text-sm" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}

            <div className="mt-6">
              <a
                href="#reserve"
                className="inline-flex items-center gap-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full px-8 py-3.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-calendar-check-line" />
                空きのある試合日を予約する
                <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-50 text-primary-600">
                  <i className="ri-arrow-right-line text-sm" />
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}